// Client Portal — RAG Customize (theme editor + embed code). Extracted from App.js.
import { useState, useEffect, useRef } from "react";
import {
  AlertCircle,
  Check,
  Clipboard,
  Code2,
  Copy,
  Edit3,
  ExternalLink,
  ImagePlus,
  Loader2,
  MessageSquare,
  Palette,
  Plus,
  Settings,
  Trash2,
  Upload,
  X,
} from "lucide-react";
import { AUTH_URL, authFetch, getToken } from "../lib/api";
import { htmlEncode, safeCssColor } from "../lib/utils";
import {
  DEFAULT_QUICK_PROMPTS,
  DEFAULT_RAG_COLORS,
  THEME_PRESETS,
  getRagConfig,
  setRagConfig,
  resizeImageToFile,
} from "../lib/ragConfig";
import { WizardShell } from "../components/WizardShell";

export const RAGCustomize = ({ facility }) => {
  const fid = facility.facility_id;
  // Server-side rag service config (facility_services.config JSONB), if present.
  // Server values win over localStorage so admins see their config across devices.
  const serverRagConfig =
    facility.services?.find((s) => s.service_type === "rag")?.config || {};
  const localConfig = getRagConfig(fid);
  const sanitizeServerImages = (raw) => {
    if (!Array.isArray(raw)) return null;
    const MATCH_MODES = new Set(["off", "keyword", "semantic"]);
    const sanitizeMatch = (m) => {
      if (!m || typeof m !== "object") return { mode: "off" };
      const mode = MATCH_MODES.has(m.mode) ? m.mode : "off";
      let threshold = Number(m.threshold);
      if (!Number.isFinite(threshold) || threshold < 0.5 || threshold > 0.99)
        threshold = 0.78;
      return {
        mode,
        pattern: typeof m.pattern === "string" ? m.pattern : "",
        description: typeof m.description === "string" ? m.description : "",
        threshold,
        syncedRuleId: typeof m.syncedRuleId === "string" ? m.syncedRuleId : "",
        syncedVectorId:
          typeof m.syncedVectorId === "string" ? m.syncedVectorId : "",
      };
    };
    return raw
      .filter(
        (img) =>
          img &&
          typeof img === "object" &&
          typeof img.url === "string" &&
          img.url,
      )
      .map((img) => ({
        id:
          typeof img.id === "string" && img.id
            ? img.id
            : `img_${Math.random().toString(36).slice(2, 10)}`,
        label: typeof img.label === "string" ? img.label : "",
        url: img.url,
        addedAt: typeof img.addedAt === "string" ? img.addedAt : "",
        match: sanitizeMatch(img.match),
      }));
  };
  const initialLogoUrl =
    (typeof serverRagConfig.logo_url === "string" &&
      serverRagConfig.logo_url) ||
    localConfig.logoUrl ||
    "";
  const initialBotAvatarUrl =
    (typeof serverRagConfig.bot_avatar_url === "string" &&
      serverRagConfig.bot_avatar_url) ||
    localConfig.botAvatarUrl ||
    "";
  const initialChatImages =
    sanitizeServerImages(serverRagConfig.chat_images) ||
    localConfig.chatImages ||
    [];
  const [config, setConfig] = useState(() => localConfig);
  const colors = { ...DEFAULT_RAG_COLORS, ...config.colors };
  const [topics, setTopics] = useState(() =>
    config.topics && Object.keys(config.topics).length
      ? config.topics
      : { ...DEFAULT_QUICK_PROMPTS },
  );
  const [saved, setSaved] = useState(false);
  const [showEmbed, setShowEmbed] = useState(false);
  const [newCategory, setNewCategory] = useState("");
  const [newPrompts, setNewPrompts] = useState({});
  const [logoUrl, setLogoUrl] = useState(initialLogoUrl);
  const [botAvatarUrl, setBotAvatarUrl] = useState(initialBotAvatarUrl);
  const [uploading, setUploading] = useState({});
  const [uploadError, setUploadError] = useState("");
  const [widgetWidthPct, setWidgetWidthPct] = useState(
    config.widgetWidthPct || 100,
  );
  const [widgetHeightPct, setWidgetHeightPct] = useState(
    config.widgetHeightPct || 70,
  );
  const [borderRadius, setBorderRadius] = useState(config.borderRadius || 12);
  const [fontSize, setFontSize] = useState(config.fontSize || 13);
  const [showSidebar, setShowSidebar] = useState(config.showSidebar !== false);
  const [requireLogin, setRequireLogin] = useState(
    config.requireLogin || false,
  );
  const [mobilePreview, setMobilePreview] = useState(false);
  const [chatImages, setChatImages] = useState(() => initialChatImages);
  const [newChatImageLabel, setNewChatImageLabel] = useState("");
  const [copiedSnippetId, setCopiedSnippetId] = useState("");

  // Autosave topics to localStorage whenever they change
  useEffect(() => {
    setRagConfig(fid, { ...getRagConfig(fid), topics });
  }, [fid, topics]);

  // Autosave chat images so admins don't lose uploads on navigation
  useEffect(() => {
    setRagConfig(fid, { ...getRagConfig(fid), chatImages });
  }, [fid, chatImages]);

  // Server-sync chat images (debounced) so admins see them across devices.
  const chatImagesHydratedRef = useRef(false);
  useEffect(() => {
    // Skip the initial mount — we only want to sync user-initiated changes.
    if (!chatImagesHydratedRef.current) {
      chatImagesHydratedRef.current = true;
      return;
    }
    const handle = setTimeout(() => {
      authFetch("/rag/ui-config", {
        method: "PUT",
        body: JSON.stringify({
          facility_id: fid,
          chat_images: chatImages,
        }),
      }).catch(() => {});
    }, 600);
    return () => clearTimeout(handle);
  }, [fid, chatImages]);

  const uploadImage = async (field, file) => {
    if (!file) return;
    setUploadError("");
    setUploading((u) => ({ ...u, [field]: true }));
    // Logo: header asset (~32px tall); avatar: shown at 28x28. Cap source dims
    // so we don't store multi-MB images for tiny render targets.
    const resizeOpts =
      field === "logo"
        ? { maxWidth: 512, maxHeight: 512 }
        : { maxWidth: 256, maxHeight: 256 };
    const resized = await resizeImageToFile(file, resizeOpts);
    const formData = new FormData();
    formData.append("file", resized);
    formData.append("facility_id", fid);
    try {
      const token = getToken();
      if (!token) {
        setUploadError("Session expired — please log in again.");
        setUploading((u) => ({ ...u, [field]: false }));
        return;
      }
      const res = await fetch(`${AUTH_URL}/upload/image`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      if (res.status === 401) {
        setUploadError("Session expired — please log in again.");
        setUploading((u) => ({ ...u, [field]: false }));
        return;
      }
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        setUploadError(err.error || `Upload failed (${res.status})`);
        setUploading((u) => ({ ...u, [field]: false }));
        return;
      }
      const data = await res.json();
      if (data.url) {
        if (field === "logo")
          setLogoUrl(`${AUTH_URL.replace("/auth", "")}${data.url}`);
        else setBotAvatarUrl(`${AUTH_URL.replace("/auth", "")}${data.url}`);
      }
    } catch (err) {
      console.error("Upload failed:", err);
      setUploadError("Upload failed — network error.");
    }
    setUploading((u) => ({ ...u, [field]: false }));
  };

  // Mobile chat content cap — matches the Lidy widget bubble width on small screens.
  // The widget itself is full-width <=620px; bubbles render at ~96% of that, so 360px
  // keeps images inside the bubble on a typical phone (375-414px viewport).
  const CHAT_IMAGE_MAX_WIDTH = 360;

  const buildChatImageHtml = (img) =>
    `<img src="${img.url}" alt="${(img.label || "").replace(/"/g, "&quot;")}" style="max-width:${CHAT_IMAGE_MAX_WIDTH}px;width:100%;height:auto;border-radius:6px;display:block;margin:6px 0;" />`;

  const uploadChatImage = async (file) => {
    if (!file) return;
    setUploadError("");
    setUploading((u) => ({ ...u, chatImage: true }));
    // Resize before upload so chat images stay mobile-appropriate. 720x1280 is
    // 2x the 360px display cap (retina sharpness without bloating storage).
    const resized = await resizeImageToFile(file, {
      maxWidth: CHAT_IMAGE_MAX_WIDTH * 2,
      maxHeight: CHAT_IMAGE_MAX_WIDTH * 2 * 2,
    });
    const formData = new FormData();
    // Prefix filename with timestamp so repeat uploads don't overwrite each other on disk.
    const ts = Date.now();
    const safeName = (resized.name || "image").replace(/[^A-Za-z0-9._-]/g, "_");
    const renamed = new File([resized], `chat_${ts}_${safeName}`, {
      type: resized.type,
    });
    formData.append("file", renamed);
    formData.append("facility_id", fid);
    try {
      const token = getToken();
      if (!token) {
        setUploadError("Session expired — please log in again.");
        setUploading((u) => ({ ...u, chatImage: false }));
        return;
      }
      const res = await fetch(`${AUTH_URL}/upload/image`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      if (res.status === 401) {
        setUploadError("Session expired — please log in again.");
        setUploading((u) => ({ ...u, chatImage: false }));
        return;
      }
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        setUploadError(err.error || `Upload failed (${res.status})`);
        setUploading((u) => ({ ...u, chatImage: false }));
        return;
      }
      const data = await res.json();
      if (data.url) {
        const fullUrl = `${AUTH_URL.replace("/auth", "")}${data.url}`;
        setChatImages((imgs) => [
          ...imgs,
          {
            id: `img_${ts}_${Math.random().toString(36).slice(2, 8)}`,
            label: newChatImageLabel.trim() || safeName,
            url: fullUrl,
            addedAt: new Date().toISOString(),
            match: {
              mode: "off",
              pattern: "",
              description: "",
              threshold: 0.78,
              syncedRuleId: "",
              syncedVectorId: "",
            },
          },
        ]);
        setNewChatImageLabel("");
      }
    } catch (err) {
      console.error("Chat image upload failed:", err);
      setUploadError("Upload failed — network error.");
    }
    setUploading((u) => ({ ...u, chatImage: false }));
  };

  const removeChatImage = (id) => {
    setChatImages((imgs) => imgs.filter((img) => img.id !== id));
  };

  const updateChatImageLabel = (id, label) => {
    setChatImages((imgs) =>
      imgs.map((img) => (img.id === id ? { ...img, label } : img)),
    );
  };

  const updateChatImageMatch = (id, matchPatch) => {
    setChatImages((imgs) =>
      imgs.map((img) =>
        img.id === id
          ? {
              ...img,
              match: {
                mode: "off",
                pattern: "",
                description: "",
                threshold: 0.78,
                syncedRuleId: "",
                syncedVectorId: "",
                ...(img.match || {}),
                ...matchPatch,
              },
            }
          : img,
      ),
    );
  };

  // Build the array of image-derived enrichment rules the server should hold.
  // Only keyword-mode entries with a non-empty pattern are turned into rules;
  // off/semantic entries don't appear here (they're handled separately).
  const buildKeywordImageRules = (imgs) =>
    imgs
      .filter(
        (img) =>
          img.match &&
          img.match.mode === "keyword" &&
          img.match.pattern &&
          img.match.pattern.trim(),
      )
      .map((img) => ({
        id: img.match.syncedRuleId || `imgrule_${img.id}`,
        label: img.label || "Chat image",
        pattern: img.match.pattern.trim(),
        html: buildChatImageHtml(img),
      }));

  const [semanticSyncStatus, setSemanticSyncStatus] = useState({
    state: "idle",
    error: "",
    upserted: 0,
    failed: 0,
  });

  // Debounced sync of image-derived enrichment rules into facilities.enrichment_rules.
  const keywordRulesHydratedRef = useRef(false);
  useEffect(() => {
    if (!keywordRulesHydratedRef.current) {
      keywordRulesHydratedRef.current = true;
      return;
    }
    const handle = setTimeout(() => {
      authFetch("/rag/image-enrichment-rules", {
        method: "PUT",
        body: JSON.stringify({
          facility_id: fid,
          rules: buildKeywordImageRules(chatImages),
        }),
      }).catch(() => {});
    }, 800);
    return () => clearTimeout(handle);
    // eslint-disable-next-line
  }, [fid, chatImages]);

  // Debounced semantic-sync. Runs whenever a chat image's semantic config
  // changes (mode, label, description). Fires only when at least one image is
  // in semantic mode OR a prior syncedVectorId would now be orphaned, since
  // those are the cases where the server needs to upsert or prune vectors.
  // The semantic-sync debounce is intentionally longer than the ui-config
  // autosave (which fires at ~600 ms) so the chat_images state on the server
  // is current before sync runs.
  const semanticSyncSignature = JSON.stringify(
    chatImages.map((img) => ({
      id: img.id,
      label: img.label || "",
      mode: img.match ? img.match.mode : "off",
      description: img.match ? img.match.description || "" : "",
      threshold: img.match ? img.match.threshold || 0.78 : 0.78,
      syncedVectorId: img.match ? img.match.syncedVectorId || "" : "",
    })),
  );
  const semanticSyncHydratedRef = useRef(false);
  useEffect(() => {
    if (!semanticSyncHydratedRef.current) {
      semanticSyncHydratedRef.current = true;
      return;
    }
    const needsSync = chatImages.some(
      (img) =>
        (img.match && img.match.mode === "semantic") ||
        (img.match && img.match.syncedVectorId),
    );
    if (!needsSync) return undefined;
    const handle = setTimeout(async () => {
      setSemanticSyncStatus((s) => ({ ...s, state: "syncing", error: "" }));
      try {
        const res = await authFetch("/rag/image-semantic-sync", {
          method: "POST",
          body: JSON.stringify({ facility_id: fid }),
        });
        if (res && res.ok) {
          setSemanticSyncStatus({
            state: "synced",
            error: "",
            upserted: res.upserted_count || 0,
            failed: res.failed_count || 0,
          });
          if (Array.isArray(res.upserted) && res.upserted.length > 0) {
            // Stash returned vector IDs back into chatImages so the next
            // signature comparison treats them as already-synced.
            const byImage = Object.fromEntries(
              res.upserted.map((u) => [u.image_id, u.vector_id]),
            );
            setChatImages((imgs) =>
              imgs.map((img) =>
                byImage[img.id]
                  ? {
                      ...img,
                      match: {
                        ...(img.match || {}),
                        syncedVectorId: byImage[img.id],
                      },
                    }
                  : img,
              ),
            );
          }
        } else {
          setSemanticSyncStatus({
            state: "error",
            error: (res && res.error) || "Sync failed",
            upserted: 0,
            failed: 0,
          });
        }
      } catch (err) {
        setSemanticSyncStatus({
          state: "error",
          error: "Network error",
          upserted: 0,
          failed: 0,
        });
      }
    }, 1500);
    return () => clearTimeout(handle);
    // eslint-disable-next-line
  }, [fid, semanticSyncSignature]);

  const copyChatImageSnippet = async (img) => {
    const html = buildChatImageHtml(img);
    try {
      await navigator.clipboard.writeText(html);
      setCopiedSnippetId(img.id);
      setTimeout(() => setCopiedSnippetId(""), 1500);
    } catch {
      setUploadError("Could not copy — copy the snippet manually.");
    }
  };

  const updateColor = (key, value) => {
    const next = { ...config, colors: { ...colors, [key]: value } };
    setConfig(next);
  };

  const applyPreset = (preset) => {
    const next = { ...config, colors: { ...preset.colors } };
    setConfig(next);
  };

  const saveConfig = () => {
    setRagConfig(fid, {
      ...config,
      topics,
      logoUrl,
      botAvatarUrl,
      widgetWidthPct,
      widgetHeightPct,
      borderRadius,
      fontSize,
      showSidebar,
      requireLogin,
      chatImages,
    });
    // Persist all widget config to server — powers the persistent embed loader
    authFetch("/rag/ui-config", {
      method: "PUT",
      body: JSON.stringify({
        facility_id: fid,
        quick_prompts: topics,
        bot_avatar_url: botAvatarUrl || "",
        logo_url: logoUrl || "",
        chat_images: chatImages,
        colors: config.colors,
        widget_width_pct: widgetWidthPct,
        widget_height_pct: widgetHeightPct,
        border_radius: borderRadius,
        font_size: fontSize,
        show_sidebar: showSidebar,
        require_login: requireLogin,
      }),
    }).catch(() => {});
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  const addCategory = () => {
    const cat = newCategory.trim();
    if (!cat || topics[cat]) return;
    setTopics({ ...topics, [cat]: [] });
    setNewCategory("");
  };

  const removeCategory = (cat) => {
    const next = { ...topics };
    delete next[cat];
    setTopics(next);
  };

  const addPrompt = (cat) => {
    const text = (newPrompts[cat] || "").trim();
    if (!text) return;
    setTopics({ ...topics, [cat]: [...topics[cat], text] });
    setNewPrompts({ ...newPrompts, [cat]: "" });
  };

  const removePrompt = (cat, idx) => {
    setTopics({
      ...topics,
      [cat]: topics[cat].filter((_, i) => i !== idx),
    });
  };

  const generateEmbedCode = () => {
    const sc = {};
    for (const [k, v] of Object.entries({
      ...DEFAULT_RAG_COLORS,
      ...config.colors,
    })) {
      sc[k] = safeCssColor(v);
    }
    const safeName = htmlEncode(facility.agent_name || facility.facility_name);
    const safeFid = JSON.stringify(fid);
    const safeAuth = JSON.stringify(AUTH_URL);
    const safeLogoUrl = logoUrl ? htmlEncode(logoUrl) : "";
    const safeBotAvatar = botAvatarUrl ? htmlEncode(botAvatarUrl) : "";
    const w = Math.max(1, Math.min(100, widgetWidthPct));
    const h = Math.max(1, Math.min(100, widgetHeightPct));
    const br = Math.max(0, Math.min(32, borderRadius));
    const fs = Math.max(10, Math.min(14, fontSize));
    const sidebarSections = Object.entries(topics).filter(
      ([, prompts]) => prompts.length > 0,
    );
    const sidebarHtml = sidebarSections
      .map(
        ([cat, prompts]) =>
          `<div style="font-size:${Math.max(9, fs - 3)}px;font-weight:700;color:${sc.accent};margin:0 0 4px;text-transform:uppercase;letter-spacing:.5px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis">${htmlEncode(cat)}</div>` +
          prompts
            .map(
              (p) =>
                `<button data-prompt="${htmlEncode(p)}" onclick="sendMsg(this.dataset.prompt)" style="width:100%;text-align:left;padding:5px 8px;margin-bottom:4px;background:${sc.accent}18;color:${sc.text};border:1px solid ${sc.accent}30;border-radius:6px;font-size:${Math.max(9, fs - 2)}px;cursor:pointer;font-family:inherit;line-height:1.3;white-space:normal;display:block">${htmlEncode(p)}</button>`,
            )
            .join(""),
      )
      .join(`<div style="height:6px"></div>`);
    const hasSidebar = sidebarSections.length > 0;

    const avatarHtml = safeBotAvatar
      ? `<img src="${safeBotAvatar}" alt="" style="width:28px;height:28px;border-radius:50%;object-fit:cover;flex-shrink:0" onerror="this.style.display='none'" />`
      : `<div style="width:28px;height:28px;border-radius:50%;background:${sc.accent};display:flex;align-items:center;justify-content:center;color:#fff;font-size:10px;font-weight:bold;flex-shrink:0">AI</div>`;

    const bbr = br > 8 ? 8 : br;
    const btnSt =
      "background:rgba(255,255,255,.15);border:1px solid rgba(255,255,255,.3);color:#fff;padding:5px 9px;cursor:pointer;border-radius:6px;font-size:12px;font-weight:600;font-family:inherit;line-height:1";
    const inSt = `width:100%;max-width:280px;padding:10px 14px;margin-bottom:8px;background:${sc.chatBg};color:${sc.headings};border:1px solid ${sc.accent}40;border-radius:${bbr}px;font-size:${fs}px;outline:none;font-family:inherit;box-sizing:border-box`;
    const authOverlay = requireLogin
      ? `
  <div id="hyve-auth" style="position:absolute;inset:0;background:${sc.bg};display:flex;flex-direction:column;align-items:center;justify-content:center;z-index:10;border-radius:${br}px;padding:32px">
    ${safeLogoUrl ? `<img src="${safeLogoUrl}" alt="" style="height:32px;margin-bottom:12px" onerror="this.style.display='none'" />` : ""}
    <div style="font-size:${fs + 2}px;font-weight:700;color:${sc.headings};margin-bottom:2px">${safeName}</div>
    <div style="display:flex;gap:0;margin:14px 0 0;border-bottom:2px solid ${sc.accent}30;width:100%;max-width:280px">
      <button id="hyve-tab-in" onclick="hyveSwitchTab('in')" style="flex:1;padding:8px 0;font-size:13px;font-weight:600;background:none;border:none;cursor:pointer;color:${sc.accent};border-bottom:2px solid ${sc.accent};margin-bottom:-2px;font-family:inherit">Sign In</button>
      <button id="hyve-tab-reg" onclick="hyveSwitchTab('reg')" style="flex:1;padding:8px 0;font-size:13px;font-weight:600;background:none;border:none;cursor:pointer;color:${sc.text};border-bottom:2px solid transparent;margin-bottom:-2px;font-family:inherit">Register</button>
    </div>
    <div id="hyve-pane-in" style="display:flex;flex-direction:column;align-items:center;width:100%;margin-top:12px">
      <input id="hyve-si-email" type="email" placeholder="Email" style="${inSt}" />
      <input id="hyve-si-pw" type="password" placeholder="Password" onkeydown="if(event.key==='Enter')hyveLogin()" style="${inSt}" />
      <button onclick="hyveLogin()" style="width:100%;max-width:280px;padding:10px 14px;background:${sc.accent};color:#fff;border:none;border-radius:${bbr}px;cursor:pointer;font-size:${fs}px;font-weight:600;font-family:inherit">Sign In</button>
      <button onclick="hyveSwitchTab('fp')" style="margin-top:8px;background:none;border:none;cursor:pointer;color:${sc.accent};font-size:${fs - 2}px;font-family:inherit;text-decoration:underline">Forgot password?</button>
    </div>
    <div id="hyve-pane-reg" style="display:none;flex-direction:column;align-items:center;width:100%;margin-top:12px">
      <input id="hyve-reg-fn" type="text" placeholder="First name" style="${inSt}" />
      <input id="hyve-reg-ln" type="text" placeholder="Last name" style="${inSt}" />
      <input id="hyve-reg-email" type="email" placeholder="Email" style="${inSt}" />
      <input id="hyve-reg-pw" type="password" placeholder="Password (min 8 characters)" onkeydown="if(event.key==='Enter')hyveRegister()" style="${inSt}" />
      <button onclick="hyveRegister()" style="width:100%;max-width:280px;padding:10px 14px;background:${sc.accent};color:#fff;border:none;border-radius:${bbr}px;cursor:pointer;font-size:${fs}px;font-weight:600;font-family:inherit">Create Account</button>
    </div>
    <div id="hyve-pane-fp" style="display:none;flex-direction:column;align-items:center;width:100%;margin-top:12px">
      <p style="font-size:${fs - 1}px;color:${sc.text};text-align:center;margin:0 0 10px;max-width:280px">Enter your email and we&#39;ll send a reset link.</p>
      <input id="hyve-fp-email" type="email" placeholder="Email" style="${inSt}" />
      <button onclick="hyveForgotPw()" style="width:100%;max-width:280px;padding:10px 14px;background:${sc.accent};color:#fff;border:none;border-radius:${bbr}px;cursor:pointer;font-size:${fs}px;font-weight:600;font-family:inherit">Send Reset Link</button>
      <button onclick="hyveSwitchTab('in')" style="margin-top:8px;background:none;border:none;cursor:pointer;color:${sc.accent};font-size:${fs - 2}px;font-family:inherit;text-decoration:underline">Back to Sign In</button>
    </div>
    <div id="hyve-auth-err" style="color:#f87171;font-size:${fs - 2}px;margin-top:10px;display:none;text-align:center;max-width:280px"></div>
  </div>`
      : "";

    const headerBtns = requireLogin
      ? `<div id="hyve-hdr-btns" style="display:none;gap:4px;align-items:center">
      <button onclick="hyveSendToSelf()" title="Email conversation" style="${btnSt}">&#128231;</button>
      <button onclick="hyveNewChat()" title="New chat" style="${btnSt}">+ New</button>
      <button onclick="hyveLogout()" title="Log out" style="${btnSt}">&#9167;</button>
    </div>`
      : `<div style="display:flex;gap:4px;align-items:center">
      <button onclick="hyveNewChat()" title="New chat" style="${btnSt}">+ New</button>
    </div>`;

    const botBubSt = `background:${sc.botBubble};color:${sc.headings};padding:10px 14px;border-radius:0 ${bbr}px ${bbr}px ${bbr}px;font-size:${fs}px;border:1px solid ${sc.accent}20;max-width:75%;line-height:1.5`;
    const usrBubSt = `background:${sc.userBubble};color:${sc.headings};padding:10px 14px;border-radius:${bbr}px 0 ${bbr}px ${bbr}px;font-size:${fs}px;border:1px solid ${sc.accent}20;max-width:75%;line-height:1.5`;
    const welcomeTxt = `Hi! I'm ${safeName}. How can I help you today?`;

    return `<!-- HyveAI RAG Chat Widget -->
<style>
@media (max-width: 600px) {
  #hyve-wrapper {
    position: fixed !important;
    top: 0 !important;
    left: 0 !important;
    right: 0 !important;
    bottom: 0 !important;
    z-index: 2147483647 !important;
  }
  #hyve-widget {
    width: 100% !important;
    height: 100% !important;
    max-width: none !important;
    max-height: none !important;
    border-radius: 0 !important;
    margin: 0 !important;
  }
  #hyve-sidebar {
    display: none !important;
  }
  /* Font overrides — !important required to beat inline styles */
  #hyve-messages [data-role] { font-size: 16px !important; line-height: 1.5 !important; }
  #hyve-input, #hyve-mic, #hyve-widget input, #hyve-auth input { font-size: 16px !important; }
  #hyve-widget button, #hyve-auth button { font-size: 15px !important; }
  #hyve-sidebar button { font-size: 13px !important; }
}
</style>
<div id="hyve-wrapper">
<div id="hyve-widget" style="position:relative;width:${w}%;height:${h}%;display:flex;flex-direction:column;border-radius:${br}px;overflow:hidden;font-family:system-ui,sans-serif;background:${sc.bg};border:1px solid ${sc.accent}33;margin:0 auto">${authOverlay}
  <div style="padding:10px 14px;background:${sc.accent};display:flex;align-items:center;gap:8px;flex-shrink:0">
    ${avatarHtml}
    <div style="flex:1;min-width:0">
      <div style="font-size:${fs + 1}px;font-weight:700;color:#fff;white-space:nowrap;overflow:hidden;text-overflow:ellipsis">${safeName}</div>
    </div>
    ${headerBtns}
  </div>
  <div style="display:flex;flex:1;overflow:hidden">
    ${hasSidebar ? `<div id="hyve-sidebar" style="width:160px;min-width:140px;overflow-y:auto;padding:10px 8px;background:${sc.bg};border-right:1px solid ${sc.accent}20;flex-shrink:0">${sidebarHtml}</div>` : ""}
    <div id="hyve-messages" style="flex:1;overflow-y:auto;padding:16px;background:${sc.chatBg};display:flex;flex-direction:column;gap:10px">
      <div style="display:flex;gap:8px;align-items:flex-end">
        ${avatarHtml}
        <div data-role="bot" style="${botBubSt}">${welcomeTxt}</div>
      </div>
    </div>
  </div>
  <div style="padding:10px 5px;background:${sc.bg};border-top:1px solid ${sc.accent}20;display:flex;gap:8px;align-items:center;flex-shrink:0">
    <input id="hyve-input" placeholder="Ask a question..." onkeydown="if(event.key==='Enter')sendMsg()" style="flex:1;padding:9px 5px;background:${sc.chatBg};color:${sc.headings};border:1px solid ${sc.accent}40;border-radius:${bbr}px;font-size:${fs}px;outline:none;font-family:inherit" />
    <button id="hyve-mic" onclick="hyveVoice()" title="Voice input" style="padding:9px 5px;background:${sc.chatBg};color:${sc.headings};border:1px solid ${sc.accent}40;border-radius:${bbr}px;cursor:pointer;font-size:${fs}px;line-height:1;flex-shrink:0">&#127908;</button>
    <button onclick="sendMsg()" style="padding:9px 5px;background:${sc.accent};color:#fff;border:none;border-radius:${bbr}px;cursor:pointer;font-size:${fs}px;font-weight:600;font-family:inherit">Send</button>
  </div>
  <div style="padding:6px 12px;background:${sc.bg};border-top:1px solid ${sc.accent}15;display:flex;align-items:center;justify-content:center;gap:4px;flex-shrink:0;opacity:0.55">
    <span style="font-size:8px;color:#50545b">&copy;</span>
    <a href="https://www.hyveappliedintelligence.com" target="_blank" rel="noopener noreferrer" style="display:inline-flex;align-items:center;"><img src="https://app.hyveappliedintelligence.com/logo-white.png" alt="HyveAI" style="height:14px;filter:brightness(0) opacity(0.55)" onerror="this.style.display='none'" /></a>
  </div>
</div>
</div>
<script>
(function(){
  // Inject viewport meta if missing — fixes Android Chrome rendering at 980px layout width
  if(!document.querySelector('meta[name="viewport"]')){
    var vm=document.createElement('meta');vm.name='viewport';
    vm.content='width=device-width,initial-scale=1,maximum-scale=1';
    document.head.appendChild(vm);
  }
  var AUTH=${safeAuth},FID=${safeFid},TOKEN=null,USER_EMAIL=null;
  var BB=${JSON.stringify(botBubSt)},UB=${JSON.stringify(usrBubSt)};
  function hyveSwitchTab(t){
    var ip=document.getElementById('hyve-pane-in'),rp=document.getElementById('hyve-pane-reg'),fp=document.getElementById('hyve-pane-fp');
    var ti=document.getElementById('hyve-tab-in'),tr=document.getElementById('hyve-tab-reg');
    var ac=${JSON.stringify(sc.accent)},tx=${JSON.stringify(sc.text)};
    var err=document.getElementById('hyve-auth-err');err.style.display='none';err.style.color='#f87171';
    ip.style.display='none';rp.style.display='none';if(fp)fp.style.display='none';
    ti.style.color=tx;ti.style.borderBottomColor='transparent';
    tr.style.color=tx;tr.style.borderBottomColor='transparent';
    if(t==='in'){ip.style.display='flex';ti.style.color=ac;ti.style.borderBottomColor=ac;}
    else if(t==='reg'){rp.style.display='flex';tr.style.color=ac;tr.style.borderBottomColor=ac;}
    else if(t==='fp'&&fp){fp.style.display='flex';}
  }
  function hyveOnAuth(token,email){
    TOKEN=token;USER_EMAIL=email||null;
    document.getElementById('hyve-auth').style.display='none';
    var b=document.getElementById('hyve-hdr-btns');if(b)b.style.display='flex';
  }
  function hyveLogin(){
    var em=document.getElementById('hyve-si-email').value.trim();
    var pw=document.getElementById('hyve-si-pw').value;
    var err=document.getElementById('hyve-auth-err');
    if(!em||!pw){err.textContent='Enter email and password';err.style.display='block';return;}
    fetch(AUTH+'/login',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({email:em,password:pw,facility_id:FID})})
    .then(function(r){return r.json();}).then(function(d){
      if(d.token){hyveOnAuth(d.token,d.user&&d.user.email);}
      else if(d.pending){
        err.style.cssText='display:block;color:#1d4ed8;background:#dbeafe;border:1px solid #93c5fd;border-radius:8px;padding:10px 12px;font-size:12px;text-align:center;';
        err.textContent=d.error||'Your account is pending admin approval.';
      }
      else{err.style.cssText='';err.textContent=d.error||'Login failed';err.style.display='block';}
    }).catch(function(){err.style.cssText='';err.textContent='Cannot connect to auth service';err.style.display='block';});
  }
  function hyveRegister(){
    var fn=document.getElementById('hyve-reg-fn').value.trim();
    var ln=document.getElementById('hyve-reg-ln').value.trim();
    var em=document.getElementById('hyve-reg-email').value.trim();
    var pw=document.getElementById('hyve-reg-pw').value;
    var err=document.getElementById('hyve-auth-err');
    if(!em||!pw){err.textContent='Email and password required';err.style.display='block';return;}
    if(pw.length<8){err.textContent='Password must be at least 8 characters';err.style.display='block';return;}
    fetch(AUTH+'/register',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({facility_id:FID,email:em,password:pw,first_name:fn,last_name:ln})})
    .then(function(r){return r.json();}).then(function(d){
      if(d.confirm_email){
        // Hide form, show "check your email" — do NOT redirect to sign-in
        document.getElementById('hyve-pane-reg').style.display='none';
        err.style.cssText='display:block;color:#15803d;background:#dcfce7;border:1px solid #86efac;border-radius:8px;padding:12px 14px;font-size:13px;text-align:center;';
        err.textContent=d.message||'Almost done! Please check your email and click the confirmation link.';
      } else if(d.pending){
        err.style.cssText='display:block;color:#1d4ed8;background:#dbeafe;border:1px solid #93c5fd;border-radius:8px;padding:12px 14px;font-size:13px;text-align:center;';
        err.textContent=d.message||'Registration submitted — awaiting admin approval.';
      } else if(d.token){
        hyveOnAuth(d.token,d.user&&d.user.email);
      } else{
        err.style.cssText='';err.textContent=d.error||'Registration failed';err.style.display='block';
      }
    }).catch(function(){err.style.cssText='';err.textContent='Cannot connect to auth service';err.style.display='block';});
  }
  function hyveLogout(){
    if(TOKEN)fetch(AUTH+'/logout',{method:'POST',headers:{Authorization:'Bearer '+TOKEN}}).catch(function(){});
    TOKEN=null;USER_EMAIL=null;
    var b=document.getElementById('hyve-hdr-btns');if(b)b.style.display='none';
    document.getElementById('hyve-auth').style.display='flex';
  }
  function hyveNewChat(){
    var box=document.getElementById('hyve-messages');
    while(box.firstChild)box.removeChild(box.firstChild);
    appendMsg('bot',${JSON.stringify(welcomeTxt)});
  }
  function hyveSendToSelf(){
    var email=window.prompt('Enter your email address:',USER_EMAIL||'');
    if(!email)return;
    var lines=[];
    document.querySelectorAll('#hyve-messages [data-role]').forEach(function(m){
      lines.push((m.dataset.role==='user'?'You':${JSON.stringify(safeName)})+': '+(m.textContent||'').trim());
    });
    if(!lines.length){window.alert('No messages to send yet. Start a conversation first.');return;}
    fetch(AUTH+'/send-transcript',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({email:email,transcript:lines.join('\\n\\n'),agent_name:${JSON.stringify(safeName)},facility_id:FID})})
    .then(function(r){return r.json();}).then(function(d){window.alert(d.success?'Sent! Check your inbox.':d.error||'Failed to send');})
    .catch(function(){window.alert('Connection error');});
  }
  function hyveForgotPw(){
    var em=document.getElementById('hyve-fp-email').value.trim();
    var err=document.getElementById('hyve-auth-err');
    if(!em){err.style.color='#f87171';err.textContent='Enter your email address';err.style.display='block';return;}
    fetch(AUTH+'/forgot-password',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({email:em,facility_id:FID})})
    .then(function(r){return r.json();}).then(function(){
      err.style.color='#4ade80';err.textContent='Reset link sent! Check your inbox.';err.style.display='block';
    }).catch(function(){err.style.color='#f87171';err.textContent='Cannot connect';err.style.display='block';});
  }
  function hyveVoice(){
    var SR=window.SpeechRecognition||window.webkitSpeechRecognition;
    if(!SR){window.alert('Voice input is not supported in this browser');return;}
    var r=new SR();r.lang='en-US';r.interimResults=false;r.maxAlternatives=1;
    r.onresult=function(e){var t=e.results[0][0].transcript;document.getElementById('hyve-input').value=t;sendMsg();};
    r.onerror=function(e){window.alert('Microphone error: '+e.error);};
    r.start();
  }
  function sendMsg(text){
    var inp=document.getElementById('hyve-input');
    var msg=text||inp.value.trim();if(!msg)return;inp.value='';
    appendMsg('user',msg);
    var headers={'Content-Type':'application/json'};
    if(TOKEN)headers['Authorization']='Bearer '+TOKEN;
    var tid='ht'+Date.now();
    var tdiv=document.createElement('div');tdiv.id=tid;tdiv.style.cssText='display:flex;gap:8px;align-items:flex-end';
    var tbub=document.createElement('div');tbub.style.cssText=BB;tbub.textContent='...';
    tdiv.appendChild(tbub);
    var box=document.getElementById('hyve-messages');box.appendChild(tdiv);box.scrollTop=box.scrollHeight;
    fetch(AUTH+'/rag/chat',{method:'POST',headers:headers,body:JSON.stringify({facility_id:FID,chatInput:msg,sessionId:'embed-'+Date.now()})})
    .then(function(r){return r.json();}).then(function(d){
      var t=document.getElementById(tid);if(t)t.remove();
      appendMsg('bot',d.output||d.text||d.response||'No response');
    }).catch(function(e){var t=document.getElementById(tid);if(t)t.remove();appendMsg('bot','Error: '+e.message);});
  }
  function appendMsg(role,text){
    var box=document.getElementById('hyve-messages');
    var wrap=document.createElement('div');
    wrap.style.cssText='display:flex;gap:8px;align-items:flex-end'+(role==='user'?';flex-direction:row-reverse':'');
    var bub=document.createElement('div');
    bub.setAttribute('data-role',role);
    bub.style.cssText=role==='user'?UB:BB;
    if(window.innerWidth<=600||screen.width<=600){bub.style.setProperty('font-size','16px','important');}
    if(role==='bot'){bub.innerHTML=text;}else{bub.textContent=text;}
    wrap.appendChild(bub);box.appendChild(wrap);box.scrollTop=box.scrollHeight;
  }
  window.sendMsg=sendMsg;window.hyveLogin=hyveLogin;window.hyveRegister=hyveRegister;
  window.hyveLogout=hyveLogout;window.hyveNewChat=hyveNewChat;window.hyveSendToSelf=hyveSendToSelf;
  window.hyveSwitchTab=hyveSwitchTab;window.hyveForgotPw=hyveForgotPw;window.hyveVoice=hyveVoice;
  var ORIG_W='${w}%',ORIG_H='${h}%',ORIG_BR='${br}px';
  function hyveApplyMobileLayout(){
    var mobile=window.innerWidth<=600||screen.width<=600;
    var wr=document.getElementById('hyve-wrapper');
    var wi=document.getElementById('hyve-widget');
    var sb=document.getElementById('hyve-sidebar');
    if(!wr||!wi)return;
    if(mobile){
      wr.style.setProperty('position','fixed','important');
      wr.style.setProperty('top','0','important');
      wr.style.setProperty('left','0','important');
      wr.style.setProperty('right','0','important');
      wr.style.setProperty('bottom','0','important');
      wr.style.setProperty('z-index','2147483647','important');
      wi.style.setProperty('width','100%','important');
      wi.style.setProperty('height','100%','important');
      wi.style.setProperty('border-radius','0','important');
      wi.style.setProperty('margin','0','important');
      if(sb)sb.style.setProperty('display','none','important');
      // font sizes — cover every element in the widget including auth screen
      document.querySelectorAll('#hyve-widget *').forEach(function(el){el.style.setProperty('font-size','16px','important');});
    }else{
      ['position','top','left','right','bottom','z-index'].forEach(function(p){wr.style.removeProperty(p);});
      wi.style.setProperty('width',ORIG_W);
      wi.style.setProperty('height',ORIG_H);
      wi.style.setProperty('border-radius',ORIG_BR);
      wi.style.setProperty('margin','0 auto');
      if(sb)sb.style.removeProperty('display');
      // restore font sizes
      document.querySelectorAll('#hyve-widget *').forEach(function(el){el.style.removeProperty('font-size');});
    }
  }
  hyveApplyMobileLayout();
  window.addEventListener('resize',hyveApplyMobileLayout);
})();
</script>`;
  };

  const colorFields = [
    { key: "bg", label: "Background" },
    { key: "sidebar", label: "Sidebar" },
    { key: "chatBg", label: "Chat Background" },
    { key: "accent", label: "Accent" },
    { key: "text", label: "Text" },
    { key: "headings", label: "Headings" },
    { key: "userBubble", label: "User Bubble" },
    { key: "botBubble", label: "Bot Bubble" },
  ];

  const steps = [
    {
      key: "theme",
      title: "Theme",
      content: (
        <div className="space-y-6">
          {/* Theme Presets */}
          <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-5">
            <h3 className="text-sm font-semibold text-slate-800 mb-3 flex items-center">
              <Palette className="w-4 h-4 mr-2 text-blue-600" />
              Theme Presets
            </h3>
            <div className="flex flex-wrap gap-2">
              {THEME_PRESETS.map((preset) => (
                <button
                  key={preset.name}
                  onClick={() => applyPreset(preset)}
                  className="flex items-center px-3 py-2 rounded-lg border border-slate-200 hover:border-blue-400 hover:shadow-sm transition-all text-sm"
                >
                  <div
                    className="w-4 h-4 rounded-full mr-2"
                    style={{ backgroundColor: preset.colors.accent }}
                  />
                  {preset.name}
                </button>
              ))}
            </div>
          </div>

          {/* Color Pickers */}
          <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-5">
            <h3 className="text-sm font-semibold text-slate-800 mb-3">
              Custom Colors
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {colorFields.map(({ key, label }) => (
                <div key={key}>
                  <label className="block text-xs text-slate-500 mb-1">
                    {label}
                  </label>
                  <div className="flex items-center space-x-2">
                    <input
                      type="color"
                      value={colors[key]}
                      onChange={(e) => updateColor(key, e.target.value)}
                      className="w-8 h-8 rounded cursor-pointer border border-slate-300"
                    />
                    <input
                      type="text"
                      value={colors[key]}
                      onChange={(e) => updateColor(key, e.target.value)}
                      className="flex-1 px-2 py-1 border border-slate-300 rounded text-xs font-mono"
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      ),
    },
    {
      key: "branding",
      title: "Branding",
      content: (
        <div className="space-y-6">
          {/* Branding */}
          <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-5">
            <h3 className="text-sm font-semibold text-slate-800 mb-3 flex items-center">
              <Settings className="w-4 h-4 mr-2 text-blue-600" />
              Branding &amp; Images
            </h3>
            {uploadError && (
              <div className="mb-3 px-3 py-2 bg-red-50 border border-red-200 rounded text-xs text-red-700">
                {uploadError}
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Logo */}
              <div>
                <label className="block text-xs text-slate-500 mb-1">
                  Logo (header)
                </label>
                <div className="flex gap-2">
                  <input
                    type="url"
                    value={logoUrl}
                    onChange={(e) => setLogoUrl(e.target.value)}
                    placeholder="https://example.com/logo.png"
                    className="flex-1 px-3 py-2 border border-slate-300 rounded-lg text-sm"
                  />
                  <label className="flex items-center px-3 py-2 bg-slate-100 hover:bg-slate-200 border border-slate-300 rounded-lg text-sm cursor-pointer whitespace-nowrap">
                    {uploading.logo ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Upload className="w-4 h-4" />
                    )}
                    <input
                      type="file"
                      accept="image/png,image/jpeg,image/gif,image/webp,image/svg+xml"
                      className="hidden"
                      onChange={(e) => uploadImage("logo", e.target.files[0])}
                    />
                  </label>
                </div>
                {logoUrl && (
                  <img
                    src={logoUrl}
                    alt="Logo preview"
                    className="mt-2 h-8 object-contain rounded"
                    onError={(e) => {
                      e.target.style.display = "none";
                    }}
                  />
                )}
              </div>
              {/* Bot Avatar */}
              <div>
                <label className="block text-xs text-slate-500 mb-1">
                  Bot Avatar
                </label>
                <div className="flex gap-2">
                  <input
                    type="url"
                    value={botAvatarUrl}
                    onChange={(e) => setBotAvatarUrl(e.target.value)}
                    placeholder="https://example.com/avatar.png"
                    className="flex-1 px-3 py-2 border border-slate-300 rounded-lg text-sm"
                  />
                  <label className="flex items-center px-3 py-2 bg-slate-100 hover:bg-slate-200 border border-slate-300 rounded-lg text-sm cursor-pointer whitespace-nowrap">
                    {uploading.avatar ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Upload className="w-4 h-4" />
                    )}
                    <input
                      type="file"
                      accept="image/png,image/jpeg,image/gif,image/webp,image/svg+xml"
                      className="hidden"
                      onChange={(e) => uploadImage("avatar", e.target.files[0])}
                    />
                  </label>
                </div>
                {botAvatarUrl && (
                  <img
                    src={botAvatarUrl}
                    alt="Avatar preview"
                    className="mt-2 w-8 h-8 object-cover rounded-full"
                    onError={(e) => {
                      e.target.style.display = "none";
                    }}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      ),
    },
    {
      key: "images",
      title: "Chat Images",
      content: (
        <div className="space-y-6">
          {/* Chat Images */}
          <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-5">
            <div className="flex items-start justify-between gap-2 mb-1">
              <h3 className="text-sm font-semibold text-slate-800 flex items-center">
                <ImagePlus className="w-4 h-4 mr-2 text-blue-600" />
                Chat Images
              </h3>
              {semanticSyncStatus.state === "syncing" && (
                <span className="inline-flex items-center gap-1 text-[11px] text-slate-500">
                  <Loader2 className="w-3 h-3 animate-spin" />
                  Syncing to Pinecone…
                </span>
              )}
              {semanticSyncStatus.state === "synced" &&
                semanticSyncStatus.upserted > 0 && (
                  <span className="inline-flex items-center gap-1 text-[11px] text-green-700">
                    <Check className="w-3 h-3" />
                    {semanticSyncStatus.upserted} synced
                    {semanticSyncStatus.failed > 0
                      ? ` · ${semanticSyncStatus.failed} failed`
                      : ""}
                  </span>
                )}
              {semanticSyncStatus.state === "error" && (
                <span
                  className="inline-flex items-center gap-1 text-[11px] text-red-600"
                  title={semanticSyncStatus.error}
                >
                  <AlertCircle className="w-3 h-3" />
                  Sync failed
                </span>
              )}
            </div>
            <p className="text-xs text-slate-500 mb-3">
              Upload images for use in chat responses. Each image is capped at{" "}
              <span className="font-medium">{CHAT_IMAGE_MAX_WIDTH}px</span> wide
              so it fits inside a mobile chat bubble. Choose <em>Keyword</em>{" "}
              for regex-trigger matching or <em>Semantic</em> for Pinecone
              vector search over the image's label + description.
            </p>
            <div className="flex flex-col sm:flex-row gap-2 mb-4">
              <input
                type="text"
                value={newChatImageLabel}
                onChange={(e) => setNewChatImageLabel(e.target.value)}
                placeholder="Label (optional, e.g. 'Polling place map')"
                className="flex-1 px-3 py-2 border border-slate-300 rounded-lg text-sm"
              />
              <label className="inline-flex items-center justify-center gap-2 px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm cursor-pointer whitespace-nowrap">
                {uploading.chatImage ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Upload className="w-4 h-4" />
                )}
                <span>Upload image</span>
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/gif,image/webp,image/svg+xml"
                  className="hidden"
                  onChange={(e) => {
                    const f = e.target.files[0];
                    e.target.value = "";
                    uploadChatImage(f);
                  }}
                />
              </label>
            </div>
            {chatImages.length === 0 ? (
              <div className="text-xs text-slate-400 italic">
                No chat images uploaded yet.
              </div>
            ) : (
              <ul className="space-y-3">
                {chatImages.map((img) => (
                  <li
                    key={img.id}
                    className="flex flex-col sm:flex-row gap-3 p-3 border border-slate-200 rounded-lg bg-slate-50"
                  >
                    <div
                      className="flex-shrink-0 bg-white border border-slate-200 rounded p-1"
                      style={{ width: "min(100%, 180px)" }}
                    >
                      <img
                        src={img.url}
                        alt={img.label || "Chat image"}
                        style={{
                          maxWidth: "100%",
                          width: "100%",
                          height: "auto",
                          display: "block",
                          borderRadius: 4,
                        }}
                        onError={(e) => {
                          e.target.style.opacity = 0.3;
                        }}
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <input
                        type="text"
                        value={img.label}
                        onChange={(e) =>
                          updateChatImageLabel(img.id, e.target.value)
                        }
                        placeholder="Label"
                        className="w-full px-2 py-1 border border-slate-300 rounded text-sm mb-2"
                      />
                      <div className="text-[11px] text-slate-500 break-all mb-2">
                        {img.url}
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <button
                          type="button"
                          onClick={() => copyChatImageSnippet(img)}
                          className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-white border border-slate-300 rounded hover:bg-slate-100"
                        >
                          {copiedSnippetId === img.id ? (
                            <>
                              <Check className="w-3 h-3 text-green-600" />
                              Copied
                            </>
                          ) : (
                            <>
                              <Copy className="w-3 h-3" />
                              Copy HTML snippet
                            </>
                          )}
                        </button>
                        <a
                          href={img.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-white border border-slate-300 rounded hover:bg-slate-100"
                        >
                          <ExternalLink className="w-3 h-3" />
                          Open
                        </a>
                        <button
                          type="button"
                          onClick={() => removeChatImage(img.id)}
                          className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-white border border-red-200 text-red-600 rounded hover:bg-red-50"
                        >
                          <Trash2 className="w-3 h-3" />
                          Remove
                        </button>
                      </div>

                      {/* Matching configuration */}
                      <div className="mt-3 pt-3 border-t border-slate-200">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                            Show this image when…
                          </span>
                          <select
                            value={(img.match && img.match.mode) || "off"}
                            onChange={(e) =>
                              updateChatImageMatch(img.id, {
                                mode: e.target.value,
                              })
                            }
                            className="text-xs border border-slate-300 rounded px-2 py-1 bg-white"
                          >
                            <option value="off">Off</option>
                            <option value="keyword">Keyword (regex)</option>
                            <option value="semantic">
                              Semantic (Pinecone)
                            </option>
                          </select>
                        </div>

                        {img.match && img.match.mode === "keyword" && (
                          <div className="space-y-1">
                            <label className="block text-[11px] text-slate-500">
                              Trigger pattern (regex, case-insensitive)
                            </label>
                            <input
                              type="text"
                              value={img.match.pattern || ""}
                              onChange={(e) =>
                                updateChatImageMatch(img.id, {
                                  pattern: e.target.value,
                                })
                              }
                              placeholder="polling place|where do i vote|ballot drop"
                              className="w-full px-2 py-1 border border-slate-300 rounded text-xs font-mono"
                            />
                            <p className="text-[11px] text-slate-400">
                              Image is injected when the user query or response
                              matches this pattern. Synced to the facility's
                              enrichment rules automatically.
                            </p>
                          </div>
                        )}

                        {img.match && img.match.mode === "semantic" && (
                          <div className="space-y-2">
                            <div>
                              <label className="block text-[11px] text-slate-500">
                                Description / extra terms (used for embedding)
                              </label>
                              <textarea
                                value={img.match.description || ""}
                                onChange={(e) =>
                                  updateChatImageMatch(img.id, {
                                    description: e.target.value,
                                  })
                                }
                                placeholder="Polling locations and ballot drop boxes in LD4. Voters ask about where to vote, drop-off sites, hours, deadlines."
                                rows={2}
                                className="w-full px-2 py-1 border border-slate-300 rounded text-xs"
                              />
                            </div>
                            <div className="flex items-center gap-2">
                              <label className="text-[11px] text-slate-500">
                                Similarity threshold
                              </label>
                              <input
                                type="number"
                                min={0.5}
                                max={0.99}
                                step={0.01}
                                value={img.match.threshold ?? 0.78}
                                onChange={(e) => {
                                  const v = parseFloat(e.target.value);
                                  updateChatImageMatch(img.id, {
                                    threshold: Number.isFinite(v)
                                      ? Math.min(0.99, Math.max(0.5, v))
                                      : 0.78,
                                  });
                                }}
                                className="w-20 px-2 py-1 border border-slate-300 rounded text-xs"
                              />
                              <span className="text-[11px] text-slate-400">
                                higher = stricter
                              </span>
                            </div>
                            {img.match.syncedVectorId ? (
                              <p className="text-[11px] text-green-700 bg-green-50 border border-green-200 rounded px-2 py-1">
                                Indexed in Pinecone
                                {img.match.syncedAt
                                  ? ` · ${new Date(img.match.syncedAt).toLocaleString()}`
                                  : ""}
                              </p>
                            ) : (
                              <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">
                                Pending index — will sync on next save.
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      ),
    },
    {
      key: "layout",
      title: "Layout",
      content: (
        <div className="space-y-6">
          {/* Layout */}
          <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-5">
            <h3 className="text-sm font-semibold text-slate-800 mb-3 flex items-center">
              <Edit3 className="w-4 h-4 mr-2 text-blue-600" />
              Layout &amp; Sizing
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-xs text-slate-500 mb-1">
                  Width (% of container)
                </label>
                <div className="flex items-center gap-1">
                  <input
                    type="number"
                    min={1}
                    max={100}
                    value={widgetWidthPct}
                    onChange={(e) =>
                      setWidgetWidthPct(
                        Math.max(1, Math.min(100, Number(e.target.value))),
                      )
                    }
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
                  />
                  <span className="text-xs text-slate-400">%</span>
                </div>
              </div>
              <div>
                <label className="block text-xs text-slate-500 mb-1">
                  Height (% of container)
                </label>
                <div className="flex items-center gap-1">
                  <input
                    type="number"
                    min={1}
                    max={100}
                    value={widgetHeightPct}
                    onChange={(e) =>
                      setWidgetHeightPct(
                        Math.max(1, Math.min(100, Number(e.target.value))),
                      )
                    }
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
                  />
                  <span className="text-xs text-slate-400">%</span>
                </div>
              </div>
              <div>
                <label className="block text-xs text-slate-500 mb-1">
                  Border Radius (px)
                </label>
                <input
                  type="number"
                  min={0}
                  max={32}
                  value={borderRadius}
                  onChange={(e) => setBorderRadius(Number(e.target.value))}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
                />
              </div>
              <div>
                <label className="block text-xs text-slate-500 mb-1">
                  Font Size (px)
                </label>
                <input
                  type="number"
                  min={10}
                  max={20}
                  value={fontSize}
                  onChange={(e) => setFontSize(Number(e.target.value))}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
                />
              </div>
            </div>
            <div className="mt-3 flex items-center space-x-6">
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={showSidebar}
                  onChange={(e) => setShowSidebar(e.target.checked)}
                  className="rounded"
                />
                <span className="text-sm text-slate-700">
                  Show quick prompts sidebar
                </span>
              </label>
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={requireLogin}
                  onChange={(e) => setRequireLogin(e.target.checked)}
                  className="rounded"
                />
                <span className="text-sm text-slate-700">
                  Require login for embed chat
                </span>
              </label>
            </div>
          </div>
        </div>
      ),
    },
    {
      key: "prompts",
      title: "Quick Prompts",
      content: (
        <div className="space-y-6">
          {/* Quick Prompts Editor */}
          <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-5">
            <h3 className="text-sm font-semibold text-slate-800 mb-3 flex items-center">
              <MessageSquare className="w-4 h-4 mr-2 text-blue-600" />
              Quick Prompts
            </h3>
            <div className="space-y-4">
              {Object.entries(topics).map(([category, prompts]) => (
                <div
                  key={category}
                  className="border border-slate-200 rounded-lg p-3"
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-semibold text-slate-700 uppercase tracking-wider">
                      {category}
                    </span>
                    <button
                      onClick={() => removeCategory(category)}
                      className="p-1 text-red-400 hover:text-red-600 hover:bg-red-50 rounded"
                      title="Remove category"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  <div className="space-y-1.5">
                    {prompts.map((p, i) => (
                      <div
                        key={i}
                        className="flex items-center justify-between px-3 py-1.5 bg-slate-50 rounded text-xs text-slate-600"
                      >
                        <span>{p}</span>
                        <button
                          onClick={() => removePrompt(category, i)}
                          className="p-0.5 text-red-400 hover:text-red-600"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                  <div className="flex items-center mt-2 space-x-2">
                    <input
                      value={newPrompts[category] || ""}
                      onChange={(e) =>
                        setNewPrompts({
                          ...newPrompts,
                          [category]: e.target.value,
                        })
                      }
                      onKeyDown={(e) =>
                        e.key === "Enter" && addPrompt(category)
                      }
                      placeholder="Add a prompt..."
                      className="flex-1 px-2 py-1 border border-slate-300 rounded text-xs"
                    />
                    <button
                      onClick={() => addPrompt(category)}
                      className="p-1 bg-blue-600 text-white rounded hover:bg-blue-700"
                    >
                      <Plus className="w-3 h-3" />
                    </button>
                  </div>
                </div>
              ))}
              <div className="flex items-center space-x-2">
                <input
                  value={newCategory}
                  onChange={(e) => setNewCategory(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && addCategory()}
                  placeholder="New category name..."
                  className="flex-1 px-3 py-1.5 border border-slate-300 rounded-lg text-xs"
                />
                <button
                  onClick={addCategory}
                  className="flex items-center px-3 py-1.5 bg-slate-100 text-slate-700 text-xs rounded-lg hover:bg-slate-200"
                >
                  <Plus className="w-3 h-3 mr-1" />
                  Category
                </button>
              </div>
            </div>
          </div>
        </div>
      ),
    },
    {
      key: "deploy",
      title: "Preview & Deploy",
      content: (
        <div className="space-y-6">
          {/* Live Preview */}
          <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-5">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-slate-800">
                Live Preview
              </h3>
              <button
                onClick={() => setMobilePreview((v) => !v)}
                className={`text-xs px-3 py-1 rounded border transition-colors ${mobilePreview ? "bg-blue-600 text-white border-blue-600" : "text-slate-500 border-slate-300 hover:border-blue-400"}`}
              >
                {mobilePreview ? "Mobile" : "Desktop"}
              </button>
            </div>
            <div
              className="overflow-hidden mx-auto"
              style={{
                backgroundColor: colors.bg,
                border: mobilePreview ? "none" : `1px solid ${colors.accent}33`,
                borderRadius: mobilePreview ? 0 : `${borderRadius}px`,
                width: mobilePreview ? "100%" : `${widgetWidthPct}%`,
                maxWidth: mobilePreview ? "375px" : undefined,
                height: mobilePreview ? "480px" : "220px",
                display: "flex",
              }}
            >
              {showSidebar && !mobilePreview && (
                <div
                  style={{
                    width: "140px",
                    backgroundColor: colors.sidebar,
                    borderRight: `1px solid ${colors.accent}33`,
                    padding: "12px",
                    flexShrink: 0,
                  }}
                >
                  <div
                    style={{
                      fontSize: `${Math.max(fontSize - 3, 9)}px`,
                      fontWeight: 600,
                      color: colors.headings,
                    }}
                  >
                    Quick Prompts
                  </div>
                  <div
                    style={{
                      marginTop: "8px",
                      padding: "6px 8px",
                      borderRadius: "6px",
                      backgroundColor: `${colors.accent}20`,
                      border: `1px solid ${colors.accent}25`,
                      fontSize: `${Math.max(fontSize - 4, 8)}px`,
                      color: colors.text,
                    }}
                  >
                    Sample prompt...
                  </div>
                </div>
              )}
              <div
                style={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  minWidth: 0,
                }}
              >
                <div
                  style={{
                    padding: "8px 12px",
                    backgroundColor: colors.sidebar,
                    borderBottom: `1px solid ${colors.accent}33`,
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                  }}
                >
                  {logoUrl ? (
                    <img
                      src={logoUrl}
                      alt=""
                      style={{ height: "20px", objectFit: "contain" }}
                      onError={(e) => {
                        e.target.style.display = "none";
                      }}
                    />
                  ) : null}
                  <span
                    style={{
                      fontSize: `${Math.max(fontSize - 1, 10)}px`,
                      fontWeight: 600,
                      color: colors.headings,
                    }}
                  >
                    {facility.facility_name}
                  </span>
                </div>
                <div
                  style={{
                    flex: 1,
                    backgroundColor: colors.chatBg,
                    padding: "12px",
                    display: "flex",
                    flexDirection: "column",
                    gap: "8px",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      gap: "6px",
                      alignItems: "flex-end",
                    }}
                  >
                    {botAvatarUrl ? (
                      <img
                        src={botAvatarUrl}
                        alt=""
                        style={{
                          width: "24px",
                          height: "24px",
                          borderRadius: "50%",
                          objectFit: "cover",
                          flexShrink: 0,
                        }}
                        onError={(e) => {
                          e.target.style.display = "none";
                        }}
                      />
                    ) : (
                      <div
                        style={{
                          width: "24px",
                          height: "24px",
                          borderRadius: "50%",
                          backgroundColor: colors.accent,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: "#fff",
                          fontSize: "9px",
                          fontWeight: "bold",
                          flexShrink: 0,
                        }}
                      >
                        AI
                      </div>
                    )}
                    <div
                      style={{
                        padding: "8px 10px",
                        borderRadius: `0 ${Math.min(borderRadius, 8)}px ${Math.min(borderRadius, 8)}px ${Math.min(borderRadius, 8)}px`,
                        backgroundColor: colors.botBubble,
                        border: `1px solid ${colors.accent}30`,
                        fontSize: `${Math.max(fontSize - 2, 9)}px`,
                        color: colors.headings,
                      }}
                    >
                      Hello! How can I help?
                    </div>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      gap: "6px",
                      alignItems: "flex-end",
                      justifyContent: "flex-end",
                    }}
                  >
                    <div
                      style={{
                        padding: "8px 10px",
                        borderRadius: `${Math.min(borderRadius, 8)}px 0 ${Math.min(borderRadius, 8)}px ${Math.min(borderRadius, 8)}px`,
                        backgroundColor: colors.userBubble,
                        border: `1px solid ${colors.accent}30`,
                        fontSize: `${Math.max(fontSize - 2, 9)}px`,
                        color: colors.headings,
                      }}
                    >
                      Tell me about the docs
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Embed toggle */}
          <div className="flex items-center justify-end">
            <button
              onClick={() => setShowEmbed(!showEmbed)}
              className="flex items-center px-4 py-2.5 bg-slate-800 text-white text-sm font-medium rounded-lg hover:bg-slate-700 transition-colors"
            >
              <Code2 className="w-4 h-4 mr-2" />
              {showEmbed ? "Hide Embed Code" : "Get Embed Code"}
            </button>
          </div>

          {/* Embed Code Panel */}
          {showEmbed &&
            (() => {
              const persistentSnippet = `<script\n  src="https://app.hyveappliedintelligence.com/static/hyve-apps/hyve-inline.js"\n  data-facility-id="${fid}"\n></script>`;
              return (
                <div className="bg-slate-900 rounded-lg border border-slate-700 p-4 space-y-4">
                  {/* Persistent embed — recommended */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <h4 className="text-sm font-semibold text-slate-200">
                          Persistent Embed
                        </h4>
                        <p className="text-xs text-slate-400 mt-0.5">
                          Add once — saves automatically update the widget. No
                          re-copying needed.
                        </p>
                      </div>
                      <button
                        onClick={() =>
                          navigator.clipboard.writeText(persistentSnippet)
                        }
                        className="flex items-center px-3 py-1 bg-blue-700 text-blue-100 text-xs rounded hover:bg-blue-600 shrink-0 ml-4"
                      >
                        <Clipboard className="w-3 h-3 mr-1.5" />
                        Copy
                      </button>
                    </div>
                    <textarea
                      readOnly
                      value={persistentSnippet}
                      className="w-full h-20 bg-slate-800 text-green-400 text-xs font-mono p-3 rounded border border-slate-600 resize-none"
                    />
                  </div>

                  {/* Divider */}
                  <div className="flex items-center gap-3">
                    <div className="flex-1 border-t border-slate-700" />
                    <span className="text-xs text-slate-500">
                      or use a self-contained snapshot
                    </span>
                    <div className="flex-1 border-t border-slate-700" />
                  </div>

                  {/* Full embed — legacy */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <h4 className="text-sm font-semibold text-slate-400">
                          Full Embed Code
                        </h4>
                        <p className="text-xs text-slate-500 mt-0.5">
                          All config baked in. Must be re-copied after each
                          save.
                        </p>
                      </div>
                      <button
                        onClick={() =>
                          navigator.clipboard.writeText(generateEmbedCode())
                        }
                        className="flex items-center px-3 py-1 bg-slate-700 text-slate-200 text-xs rounded hover:bg-slate-600 shrink-0 ml-4"
                      >
                        <Clipboard className="w-3 h-3 mr-1.5" />
                        Copy
                      </button>
                    </div>
                    <textarea
                      readOnly
                      value={generateEmbedCode()}
                      className="w-full h-48 bg-slate-800 text-slate-400 text-xs font-mono p-3 rounded border border-slate-700 resize-none"
                    />
                  </div>
                </div>
              );
            })()}
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <WizardShell
        accent="blue"
        steps={steps}
        onFinish={saveConfig}
        finishLabel={saved ? "Saved" : "Save Configuration"}
      />
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════
// SLM — Training data review + model query
// ═══════════════════════════════════════════════════════════════════
